import { createSelector } from 'reselect';
import * as AlertBarState from '../../components/alert-bar/AlertBar.state';
import { formatCurrencyInput } from '../../utils/helpers';
import * as Nested from '../../utils/Nested';
import { SEARCH_REQUEST_SUCCESS } from '../search/Search.state';
import { v4 as uuidv4 } from 'uuid';
import { ALERT_BAR_ACTION } from '../lookup/Lookup.state';

// Default State
const initialState = {
  error: false,
  data: {
    searchResults: [],
    account: [],
  },
};

// Action Creators and Action Types
const UPDATE_RESULT_AMOUNT = 'UPDATE_RESULT_AMOUNT';
export const updateResultAmount = (id, amount) => ({
  type: UPDATE_RESULT_AMOUNT,
  payload: {
    uniqueId: id,
    newAmount: amount,
  },
});

const TOGGLE_RESULT_SELECT = 'TOGGLE_RESULT_SELECT';
export const toggleResultSelect = (id) => ({
  type: TOGGLE_RESULT_SELECT,
  payload: {
    uniqueId: id,
  },
});

const UPDATE_RESULT_ANNOTATION = 'UPDATE_RESULT_ANNOTATION';
export const updateResultAnnotation = (id, fieldName, value) => ({
  type: UPDATE_RESULT_ANNOTATION,
  payload: {
    uniqueId: id,
    fieldName,
    value,
  },
});

const TOGGLE_SELECT_ALL = 'TOGGLE_SELECT_ALL';
export const toggleSelectAll = () => ({
  type: TOGGLE_SELECT_ALL,
});

const itemReducer = (item, { type, payload }) => {
  switch (type) {
    case UPDATE_RESULT_AMOUNT:
      return {
        ...item,
        recorded_price: item.amouont,
        amount: payload.newAmount,
      };
    case TOGGLE_RESULT_SELECT:
      return {
        ...item,
        selected: !item.selected,
      };
    case UPDATE_RESULT_ANNOTATION:
      return {
        ...item,
        annotations: {
          ...item.annotations,
          [payload.fieldName]: payload.value,
        },
      };
    default:
      return item;
  }
};

// Raw Reducer
export const _reducer = (state = initialState, { type, payload } = action) => {
  switch (type) {
    case SEARCH_REQUEST_SUCCESS:
      return {
        ...state,
        /**
         * 200 response but receivables are empty means that the search was successful,
         * but there are no payments available. The user is not redirected to the results page
         * so there is no need to update the state here.
         *
         * See searchForItemEpic in Search.epic.js for more information.
         */
        data: !payload.receivables
          ? state.data
          : {
              searchResults: payload.receivables.map((r) => ({
                ...r,
                uniqueId: uuidv4(),
                amount: formatCurrencyInput(r.amount),
              })),
              account: payload.contact_info,
              tableHeaders: payload.table_headers,
            },
      };
    case TOGGLE_SELECT_ALL:
      if (
        state.data.searchResults.some((result) => !Boolean(result.selected))
      ) {
        return {
          ...state,
          data: {
            ...state.data,
            searchResults: state.data.searchResults.map((result) => ({
              ...result,
              selected: true,
            })),
          },
        };
      }
      return {
        ...state,
        data: {
          ...state.data,
          searchResults: state.data.searchResults.map((result) => ({
            ...result,
            selected: false,
          })),
        },
      };
    case UPDATE_RESULT_AMOUNT:
    case TOGGLE_RESULT_SELECT:
    case UPDATE_RESULT_ANNOTATION:
      return {
        ...state,
        data: {
          ...state.data,
          searchResults: state.data.searchResults.map((item) => {
            return item.uniqueId === payload.uniqueId
              ? itemReducer(item, { type, payload })
              : item;
          }),
        },
      };
    default:
      return state;
  }
};

// Selectors
const searchResultsSelector = (state) => state.searchResults.data.searchResults;
const accountDataSelector = (state) => state.searchResults.data.account;
const selectedResultsSelector = createSelector(searchResultsSelector, (items) =>
  items.filter((item) => Boolean(item.selected)),
);
const selectedTotalSelector = createSelector(selectedResultsSelector, (items) =>
  items.reduce((total, item) => total + Number(item.amount), 0),
);

// Raw Redux Maps
export const _mapStateToProps = (state) => ({
  ...state.searchResults,
  searchResults: searchResultsSelector(state),
  selectedResults: selectedResultsSelector(state),
  selectedResultsTotal: selectedTotalSelector(state),
  accountData: accountDataSelector(state),
});
export const _mapDispatchToProps = (dispatch) => ({
  updateResultAmount: (...args) => dispatch(updateResultAmount(...args)),
  updateResultAnnotation: (...args) =>
    dispatch(updateResultAnnotation(...args)),
  toggleResultSelect: (...args) => dispatch(toggleResultSelect(...args)),
  toggleSelectAll: () => dispatch(toggleSelectAll()),
});

// Nested Redux Maps
const {
  reducer: nestedReducer,
  mapStateToProps,
  mapDispatchToProps,
  actions,
} = Nested.nestStates(
  {
    reducer: _reducer,
    mapStateToProps: _mapStateToProps,
    mapDispatchToProps: _mapDispatchToProps,
  },
  {
    alertBars: {
      searchResultsAlertBars: {
        ...AlertBarState,
        actionType: ALERT_BAR_ACTION,
      },
    },
  },
  'searchResults',
);

// Nested Reducer
const reducer = (state, action) => nestedReducer(state, action);

export { reducer, mapStateToProps, mapDispatchToProps, actions };
